import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	changeGamePhase,
	emitUpdateMainClaim,
	emitVoteConvinced,
	emitVoteNotPersuaded,
	emitAddReasonToPlay,
	emitUpdateReasonToPlay,
	emitUpdateReasonToPlayPriorities,
	emitDeleteReasonToPlay,
	emitMoveToReasonInPlay,
	emitUpdateReasonInPlay,
	emitVoteEstablishedReasonInPlay,
	emitVoteContestedReasonInPlay,
	emitVoteNonGermaneReasonInPlay,
	emitMoveRIPToCommonGround,
	emitMoveRIPToDissensusList,
	emitChangeGamePhase,
	emitGetFinalResults,
} from "../../reducers/gameSlice";

import Scrimmage from "../containers/Scrimmage";
import MainClaim from "../containers/MainClaim";
import EditReason from "../containers/EditReason";
import ReasonsToPlay from "../containers/ReasonsToPlay";
import ReasonInPlay from "../containers/ReasonInPlay";
import CommonGrounds from "../containers/CommonGrounds";
import Results from "../containers/Results";
import ConfirmAction from "../modals/ConfirmAction";

const root = document.getElementById("root");

function Game() {

	const dispatch = useDispatch();
	const roomCode = useSelector(state => state.game.roomCode);
	const role = useSelector(state => state.game.role);
	const player = useSelector(state => state.game.player);
	const mainClaim = useSelector(state => state.game.mainClaim);
	const reasonsToPlay = useSelector(state => state.game.reasonsToPlay);
	const reasonInPlay = useSelector(state => state.game.reasonInPlay);
	const commonGrounds = useSelector(state => state.game.commonGrounds);
	const results = useSelector(state => state.game.results);

	const [reasonToEdit, setReasonToEdit] = useState(null);
	const [reasonToEditType, setReasonToEditType] = useState(null);
	const [sortedReasonsToPlay, setSortedReasonsToPlay] = useState([]);
	const [confirmActionData, setConfirmActionData] = useState({});
	const [showConfirmActionModal, setShowConfirmActionModal] = useState(false);

	const phases = [
		{ value: "land", text: "Land" },
		{ value: "strawpoll", text: "Strawpoll" },
		{ value: "saywhy", text: "SayWhy" },
		{ value: "bout", text: "Bout" },
		{ value: "endpoll", text: "Endpoll" },
	];

	const phase = mainClaim ? mainClaim.phase : "";

	useEffect(() => {
		// Ensure the current user reasonsToPlay appear first on the list of reasonsToPlay
		const currentUserReasonToPlay = reasonsToPlay.find(reasonsToPlaySet => reasonsToPlaySet.id === player.id);
		if (currentUserReasonToPlay) {
			const sortedReasons = reasonsToPlay.filter(reasonToPlaySet => reasonToPlaySet.id !== currentUserReasonToPlay.id);
			sortedReasons.sort((a, b) => {
				return a.id.localeCompare(b.id);
			});
			setSortedReasonsToPlay([currentUserReasonToPlay, ...sortedReasons]);
		} else {
			setSortedReasonsToPlay(reasonsToPlay);
		}
	}, [reasonsToPlay, player]);

	function handleConvincedVote() {
		const data = {
			roomCode: roomCode,
			playerId: player.id,
		};
		dispatch(emitVoteConvinced(data));
	}

	function handleNotPersuadedVote() {
		const data = {
			roomCode: roomCode,
			playerId: player.id,
		};
		dispatch(emitVoteNotPersuaded(data));
	}

	function handleUpdateMainClaim(mainClaim) {
		dispatch(
			emitUpdateMainClaim({
				roomCode: roomCode,
				mainClaim: mainClaim,
			})
		);
	}

	function sendNewReasonToPlay(reason) {
		dispatch(
			emitAddReasonToPlay({
				roomCode: roomCode,
				playerId: player.id,
				...reason,
			})
		);
	}

	function moveToReasonInPlay(playerId, reasonId) {
		if (phase !== "bout") {
			handleChangeGamePhase("bout");
		}
		dispatch(
			emitMoveToReasonInPlay({
				roomCode: roomCode,
				playerId: playerId,
				reasonId: reasonId,
			})
		);
	}

	function updateReasonToPlay(playerId, reasonId, reason) {
		dispatch(
			emitUpdateReasonToPlay({
				roomCode: roomCode,
				playerId: playerId,
				reasonId: reasonId,
				...reason,
			})
		);
	}

	function updateReasonToPlayPriorities(priorityList) {
		dispatch(emitUpdateReasonToPlayPriorities({ roomCode: roomCode, playerId: player.id, priorities: priorityList }));
	}

	function deleteReasonToPlay(reasonToDelete) {
		dispatch(
			emitDeleteReasonToPlay({
				roomCode: roomCode,
				playerId: reasonToDelete.playerId,
				reasonId: reasonToDelete.reasonId,
			})
		);
	}

	function updateReasonInPlay(playerId, reasonId, reason) {
		dispatch(
			emitUpdateReasonInPlay({
				roomCode: roomCode,
				playerId: playerId,
				reasonId: reasonId,
				...reason,
			})
		);
	}

	function openEditReasonForm(reasonType, playerId, reason) {
		let reasonToEdit;
		if (reasonType === "rip") {
			reasonToEdit = reasonInPlay;
		} else {
			const reasonToPlay = reasonsToPlay.find(reasonToPlay => reasonToPlay.id === playerId);
			reasonToEdit = reasonToPlay.reasons.find(element => element.id === reason.id);
		}
		setReasonToEdit(reasonToEdit);
		setReasonToEditType(reasonType);
	}

	function removeReasonToEdit() {
		setReasonToEdit(null);
		setReasonToEditType(null);
	}

	function openConfirmActionModal(actionData) {
		setConfirmActionData(actionData);
		setShowConfirmActionModal(true);
	}

	function closeConfirmActionModal() {
		setShowConfirmActionModal(false);
		setConfirmActionData({});
	}

	function handleEstablished(playerId) {
		dispatch(emitVoteEstablishedReasonInPlay({ roomCode: roomCode, playerId: playerId }));
	}

	function handleContested(playerId) {
		dispatch(emitVoteContestedReasonInPlay({ roomCode: roomCode, playerId: playerId }));
	}

	function handleNonGermane(playerId) {
		dispatch(emitVoteNonGermaneReasonInPlay({ roomCode: roomCode, playerId: playerId }));
	}

	function handleMoveRIPToCommonGround() {
		dispatch(
			emitMoveRIPToCommonGround({
				roomCode: roomCode,
			})
		);
	}

	function handleMoveRIPToDissensusList() {
		dispatch(
			emitMoveRIPToDissensusList({
				roomCode: roomCode,
			})
		);
	}

	function handleChangeGamePhase(value) {
		dispatch(changeGamePhase({ phase: value }));
		dispatch(emitChangeGamePhase({ roomCode: roomCode, phase: value }));
	}

	function requestFinalResults() {
		return new Promise(resolve => {
			dispatch(emitGetFinalResults({ roomCode: roomCode }));
			const interval = setInterval(() => {
				if (results !== null) {
					clearInterval(interval);
					resolve(results);
				}
			}, 150)
		});
	}

	function handleGetFinalResults() {
		dispatch(emitGetFinalResults({ roomCode: roomCode }));
	}

	async function handleDownloadResults() {
		const res = await requestFinalResults();
		console.log(res);
		const blob = new Blob([JSON.stringify(results, null, 4)], { type: "application/json" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `tug-o-logic-${roomCode}.json`;
		link.click();
		link.remove();
		URL.revokeObjectURL(url);
	}

	return (
		<div className="lg:relative lg:flex lg:min-h-screen">
			<div className="m-1 w-full">
				<div className="flex-1">
					<MainClaim
						role={role}
						phases={phases}
						roomCode={roomCode}
						mainClaim={mainClaim}
						player={player}
						results={results}
						handleUpdateMainClaim={handleUpdateMainClaim}
						handleConvincedVote={handleConvincedVote}
						handleNotPersuadedVote={handleNotPersuadedVote}
						handleChangeGamePhase={handleChangeGamePhase}
						handleGetFinalResults={handleGetFinalResults}
						handleDownloadResults={handleDownloadResults}
					/>
					<div className="flex flex-row w-full">
						{reasonInPlay && phase !== "endpoll" ? (
							<>
								<ReasonInPlay
									role={role}
									player={player}
									reason={reasonInPlay}
									openEditReasonForm={openEditReasonForm}
									handleEstablished={handleEstablished}
									handleContested={handleContested}
									handleNonGermane={handleNonGermane}
									openConfirmActionModal={openConfirmActionModal}
									handleMoveRIPToCommonGround={handleMoveRIPToCommonGround}
									handleMoveRIPToDissensusList={handleMoveRIPToDissensusList}
								/>
								<Scrimmage
									established={reasonInPlay.established.length}
									contested={reasonInPlay.contested.length}
									nonGermane={reasonInPlay.nonGermane.length}
								/>
							</>
						) : null}
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-3">
						{phase !== "endpoll" ? (
							<div>
								<EditReason
									player={player}
									reason={reasonToEdit}
									reasonType={reasonToEditType}
									updateReasonToPlay={updateReasonToPlay}
									updateReasonInPlay={updateReasonInPlay}
									sendNewReasonToPlay={sendNewReasonToPlay}
									removeReasonToEdit={removeReasonToEdit}
								/>
							</div>
						) : null}
						<CommonGrounds commonGrounds={commonGrounds} />
					</div>

					{phase !== "endpoll" ? (
						<ReasonsToPlay
							role={role}
							playerId={player.id}
							reasonsToPlay={sortedReasonsToPlay}
							updatePriorities={updateReasonToPlayPriorities}
							openEditReasonForm={openEditReasonForm}
							openConfirmActionModal={openConfirmActionModal}
						/>
					) : null}
					{results !== null ? <Results results={results} /> : null}
				</div>
			</div>

			{showConfirmActionModal && createPortal(
				<ConfirmAction
					actionData={confirmActionData}
					deleteReason={deleteReasonToPlay}
					moveToReasonInPlay={moveToReasonInPlay}
					handleMoveRIPToCommonGround={handleMoveRIPToCommonGround}
					handleMoveRIPToDissensusList={handleMoveRIPToDissensusList}
					closeModal={closeConfirmActionModal}
				/>,
				root
			)}

		</div>
	);
}

export default Game;
